import {
    CommanderFormProps,
    SchemaFormFields,
    useDialogForm,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { OverlayedLoader } from '@mews-commander/ui';
import { useProductCategoryFormDefinition } from './formDefinitions/productCategoryFormDefinition';
import {
    ProductCategoryFormValues,
    useProductCategoryFormSchema,
    useToProductCategoryUpdateParameters,
} from './schemas/productCategoryFormSchema';

const EditProductCategoryFormContent = ({
    productCategoryId,
    ...props
}: CommanderFormProps & { productCategoryId: string }) => {
    const enterpriseId = useCurrentEnterpriseId();
    const { data, isFetching } = useRepositorySuspenseQuery(
        'productCategories',
        'getAll',
        { enterpriseIds: [enterpriseId] },
        { select: ({ productCategories }) => productCategories?.filter(({ id }) => id === productCategoryId)?.[0] }
    );

    const productCategoryFormDefinition = useProductCategoryFormDefinition(data?.imageId, productCategoryId);
    const productCategoryFormSchema = useProductCategoryFormSchema();

    const toProductCategoryUpdateParameters = useToProductCategoryUpdateParameters({ productCategoryId });
    const { mutateAsync } = useRepositoryMutation('productCategories', 'update', {
        invalidateQueries: { products: ['getOverviewEntries'], productCategories: ['getAll'] },
        onSuccess: () => {
            showSuccessAlert({ description: lt('ProductCategoryUpdatedSuccessMessage') });
        },
    });

    const { renderFormContent } = useHandledDialogFormControls<ProductCategoryFormValues>({
        ...props,
        values: data
            ? {
                  ...data,
                  parentProductCategoryId: data.parentCategoryId ?? null,
              }
            : undefined,
        onSubmit: async (values, { dirtyFields }) => {
            const params = await toProductCategoryUpdateParameters(values, dirtyFields);
            await mutateAsync({
                ...params,
            });
        },
        resolver: createZodFormResolver(productCategoryFormSchema),
    });

    return renderFormContent(
        <>
            <OverlayedLoader show={isFetching} />
            <SchemaFormFields schema={productCategoryFormSchema} formDefinition={productCategoryFormDefinition} />
        </>
    );
};

export const useEditProductCategoryDialogForm = ({ productCategoryId }: { productCategoryId: string }) =>
    useDialogForm({
        id: 'edit-product-category-form',
        title: lt('ProductCategoryFormTitleEditProductCategory'),
        saveLabel: lt('Save'),
        renderForm: (props) => <EditProductCategoryFormContent {...props} productCategoryId={productCategoryId} />,
    });
